import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, signal, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CityLink } from '../../models/city-link.interface';
import { CityEnum, CityNames } from '../../models/city.enum';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SeoService } from '../../services/seo.service';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer, Meta, Title } from '@angular/platform-browser';
import { MetaDataService } from '../../services/meta-data.service';
// Meta dynamique par ville
export const CityMeta: { 
  [key in CityEnum]: { title: string; description: string; keywords: string } 
} = {
  [CityEnum.MARRAKECH]: {
    title: 'Taxi à Marrakech | Réservation rapide 24/7 - Wetaxi',
    description: 'Réservez un taxi fiable à Marrakech 24/7 avec Wetaxi pour vos déplacements en ville, à l’aéroport ou vers toute destination touristique.',
    keywords: 'taxi marrakech, réservation taxi marrakech, wetaxi'
  },

  [CityEnum.CASABLANCA]: {
    title: 'Taxi à Casablanca | Service fiable & immédiat - Wetaxi',
    description: 'Profitez d’un service de taxi à Casablanca rapide, sécurisé et disponible 24/7 avec Wetaxi pour tous vos trajets urbains et professionnels.',
    keywords: 'taxi casablanca, réservation taxi casablanca, wetaxi'
  },

  [CityEnum.TANGER]: {
    title: 'Taxi à Tanger | Transport sécurisé & rapide - Wetaxi',
    description: 'Réservez facilement votre taxi à Tanger avec Wetaxi et bénéficiez d’un transport confortable, sécurisé et disponible à toute heure.',
    keywords: 'taxi tanger, réservation taxi tanger, wetaxi'
  },

  [CityEnum.AGADIR]: {
    title: 'Taxi à Agadir | Réservez votre trajet en ligne - Wetaxi',
    description: 'Wetaxi vous propose un service de taxi fiable à Agadir, disponible 24/7 pour vos déplacements en ville, à l’aéroport ou vers les plages.',
    keywords: 'taxi agadir, réservation taxi agadir, wetaxi'
  },

  [CityEnum.RABAT]: {
    title: 'Taxi à Rabat | Chauffeurs professionnels 24/7 - Wetaxi',
    description: 'Réservez un taxi à Rabat avec Wetaxi et profitez d’un service professionnel, ponctuel et sécurisé pour tous vos trajets personnels ou professionnels.',
    keywords: 'taxi rabat, réservation taxi rabat, wetaxi'
  }
};
@Component({
  selector: 'app-city-links',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatListModule, MatProgressSpinnerModule],
  templateUrl: './city-links.component.html',
  styleUrls: ['./city-links.component.scss'],
})
export class CityLinksComponent implements OnInit {
  @Input() set cityId(value: CityEnum) {
    this._cityId = value;
    this.cityName.set(CityNames[value]);
    // Recharger les liens quand l'ID change
    this.loadCityLinks();
    this.initCityContent(value);
  }
  get cityId(): CityEnum {
    return this._cityId;
  }
  
  private _cityId!: CityEnum;
  cityName = signal<string>('');
  
  cityLinks = signal<CityLink[]>([]);
  loading = signal<boolean>(false);
  error = signal<string | null>(null);

  constructor(
    private seoService: SeoService,
    private languageService: TranslateService,
    private titleService: Title, 
    private metaService: Meta,
    private sanitizer: DomSanitizer,
    private metaDataService: MetaDataService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() { 
    // On ne charge plus les liens ici car le setter de cityId s'en charge
  }

  private async loadCityLinks() {
    this.loading.set(true);
    this.error.set(null);
    const currentLang = this.languageService.currentLang ?? 'en';

    try {
      const res = await firstValueFrom(this.seoService.getCityLinks(this.cityId.toString(), currentLang));
      if (res && res.length > 0) {
        this.cityLinks.set(res);
      } else {
        const message = this.languageService.instant('CITY_LINKS.NO_LINKS_FOUND', { city: this.cityName() });
        this.error.set(message);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des liens:', err);
      const message = this.languageService.instant('CITY_LINKS.ERROR_LOADING_LINKS', { city: this.cityName() });
      this.error.set(message);
    } finally {
      this.loading.set(false);
    }
  }


  initCityContent(cityId: CityEnum) {
    const cityData = CityMeta[cityId];
    const cityName = CityNames[cityId];

    if (cityData) {
      // Met à jour le titre et les meta tags
      this.titleService.setTitle(cityData.title);
      this.metaService.updateTag({ name: 'description', content: cityData.description });
      this.metaService.updateTag({ name: 'keywords', content: cityData.keywords });

      // Retourne éventuellement les infos pour affichage
      return { cityName, ...cityData };
    } else {
      console.warn('City not found, fallback to Casablanca');
      const fallback = CityMeta[CityEnum.MARRAKECH];
      this.titleService.setTitle(fallback.title);
      this.metaService.updateTag({ name: 'description', content: fallback.description });
      this.metaService.updateTag({ name: 'keywords', content: fallback.keywords });
      return { cityName: CityNames[CityEnum.MARRAKECH], ...fallback };
    }
  }

}
